import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Button,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { UserDetailsStep } from './steps/UserDetailsStep';
import { AccessProvisioningStep } from './steps/AccessProvisioningStep';
import { SoftwareStep } from './steps/SoftwareStep';
import { HardwareStep } from './steps/HardwareStep';
import { AdditionalStep } from './steps/AdditionalStep';
import { ReviewStep } from './steps/ReviewStep';
import { useOnboardingForm } from '../../hooks/useOnboardingForm';
import { apiClient } from '../../api/client';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalXL,
  },
  stepper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXL,
    position: 'relative',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
    zIndex: 1,
  },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
    transition: 'all 0.2s ease-in-out',
  },
  stepActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: 'white',
  },
  stepComplete: {
    backgroundColor: '#107c10',
    color: 'white',
  },
  stepInactive: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  stepLabelActive: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  progressLine: {
    position: 'absolute',
    top: '16px',
    left: '10%',
    right: '10%',
    height: '2px',
    backgroundColor: tokens.colorNeutralBackground4,
    zIndex: 0,
  },
  content: {
    minHeight: '400px',
    marginBottom: tokens.spacingVerticalXL,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalL,
  },
  error: {
    marginBottom: tokens.spacingVerticalL,
  },
});

interface OnboardingWizardProps {
  onComplete: (requestId: string) => void;
}

const STEPS = [
  { id: 'user-details', label: 'User Details' },
  { id: 'access', label: 'Access' },
  { id: 'software', label: 'Software' },
  { id: 'hardware', label: 'Hardware' },
  { id: 'additional', label: 'Additional' },
  { id: 'review', label: 'Review' },
];

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const styles = useStyles();
  const {
    currentStep,
    formData,
    updateFormData,
    validationErrors,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
  } = useOnboardingForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      nextStep();
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await apiClient.post<{ data: { requestId: string } }>('/onboarding', {
        ...formData,
        startDate: formData.startDate?.toISOString().split('T')[0],
        endDate: formData.endDate?.toISOString().split('T')[0],
      });

      onComplete(response.data.requestId);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to submit request'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <UserDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );
      case 1:
        return (
          <AccessProvisioningStep
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 2:
        return (
          <SoftwareStep
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 3:
        return (
          <HardwareStep
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );
      case 4:
        return (
          <AdditionalStep
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 5:
        return <ReviewStep formData={formData} />;
      default:
        return null;
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <Card className={styles.card}>
      {/* Stepper */}
      <div className={styles.stepper}>
        <div className={styles.progressLine} />
        {STEPS.map((step, index) => (
          <div key={step.id} className={styles.step}>
            <div
              className={`${styles.stepCircle} ${
                index < currentStep
                  ? styles.stepComplete
                  : index === currentStep
                  ? styles.stepActive
                  : styles.stepInactive
              }`}
              onClick={() => index < currentStep && goToStep(index)}
              style={{ cursor: index < currentStep ? 'pointer' : 'default' }}
            >
              {index < currentStep ? <CheckmarkRegular /> : index + 1}
            </div>
            <Text
              className={`${styles.stepLabel} ${
                index === currentStep ? styles.stepLabelActive : ''
              }`}
            >
              {step.label}
            </Text>
          </div>
        ))}
      </div>

      {/* Error Message */}
      {submitError && (
        <MessageBar intent="error" className={styles.error}>
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Step Content */}
      <div className={styles.content}>{renderStepContent()}</div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular />}
          onClick={prevStep}
          disabled={currentStep === 0 || isSubmitting}
        >
          Back
        </Button>

        {isLastStep ? (
          <Button
            appearance="primary"
            icon={isSubmitting ? <Spinner size="tiny" /> : <CheckmarkRegular />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        ) : (
          <Button
            appearance="primary"
            icon={<ArrowRightRegular />}
            iconPosition="after"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </div>
    </Card>
  );
}
